import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, LogOut, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../contexts/AuthContext";

const NAVY = "#2a2420";
const ORANGE = "#607840";

interface PendingRequest {
  request_id: string;
  business_id: string;
  business_name: string;
  message: string | null;
  created_at: string;
}

export default function PendingRequestPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function handleBack() {
    if (typeof window !== "undefined" && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/", { replace: true });
  }

  useEffect(() => {
    if (!user) return;

    setError(null);

    supabase.rpc("get_my_pending_join_requests").then(({ data, error: rpcError }) => {
      if (rpcError) {
        setError(rpcError.message);
        setRequests([]);
      } else {
        setRequests((data as unknown as PendingRequest[]) ?? []);
      }
      setLoading(false);
    });
  }, [user]);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: "#fefcf8" }}
    >
      <div className="w-full max-w-sm text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: "rgba(96,120,64,0.1)" }}
        >
          <Clock size={26} style={{ color: ORANGE }} />
        </div>

        <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
          Request pending
        </h1>
        <p className="text-sm leading-relaxed mb-6" style={{ color: NAVY, opacity: 0.55 }}>
          Your request to join a business is waiting for approval. You'll be
          notified once an owner reviews it.
        </p>

        {loading ? (
          <Loader2 size={20} className="animate-spin mx-auto" style={{ color: ORANGE }} />
        ) : requests.length > 0 ? (
          <div className="space-y-2 text-left mb-6">
            {requests.map((r) => (
              <div
                key={r.request_id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{
                  backgroundColor: "#fefcf8",
                  border: `1.5px solid ${NAVY}1a`,
                }}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: ORANGE }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>
                    {r.business_name}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.4 }}>
                    Submitted {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p
            className="text-xs px-3 py-2 rounded-lg mb-6"
            style={{
              color: "#991b1b",
              backgroundColor: "rgba(220,38,38,0.06)",
              border: "1px solid rgba(220,38,38,0.2)",
            }}
          >
            Could not load pending requests: {error}
          </p>
        ) : null}

        <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>
          Ask your business owner to approve your request, or get an invitation
          code to join directly.
        </p>

        <button
          type="button"
          onClick={handleBack}
          className="flex items-center justify-center gap-1.5 w-full mt-6 py-2.5 rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.25)" }}
        >
          <ArrowLeft size={14} />
          Back to start
        </button>

        <button
          onClick={handleSignOut}
          className="flex items-center gap-1.5 text-sm mx-auto mt-4"
          style={{ color: NAVY, opacity: 0.4 }}
        >
          <LogOut size={13} />
          Sign out
        </button>
      </div>
    </div>
  );
}
